<template>
    <div v-loading.fullscreen.lock="fullscreenLoading" class="campaign-create">
        <div :class="['page-header', { sticky: isSticky }]">
            <h1 class="page-title">{{ isEditMode ? $t('messages.edit') : $t('messages.create_ad_group') }}</h1>
            <button class="btn-primary" @click="saveAdGroup" :disabled="(!isEditMode && !canCreate) || submitting">
                {{ isEditMode ? $t('messages.update') : $t('messages.create') }}
            </button>
        </div>

        <div class="campaign-content">
            <div class="campaign-sidebar">
                <div class="sidebar-nav">
                    <div v-for="(step, index) in steps" :key="step.id"
                        :class="['nav-item', { active: currentStep === step.id }]" @click="goToStep(step.id)">
                        <div class="nav-icon"><img :src="step.icon" :alt="step.title" /></div>
                        <div class="nav-text">{{ step.title }}</div>
                    </div>
                </div>
            </div>

            <div class="campaign-main" v-if="isInitialized">
                <div id="main-settings" class="settings-block">
                    <MainSettings :campaign="campaign" :prices="prices" :initial-form="mainSettingsData"
                        :readonly="isEditMode" @created="onCreated" @update="onMainUpdate" />
                </div>

                <div id="budget-schedule" class="settings-block">
                    <BudgetSchedule :ad-group="adGroup" :currencies="currencies"
                        :adGroupStart="mainSettingsData.start_date || null"
                        :adGroupEnd="mainSettingsData.end_date || null"
                        :kpi="mainSettingsData.kpi || ''"
                        :priceValue="mainSettingsData.price_value"
                        :is-test="isTestCampaign"
                        @update="onBudgetUpdate" />
                </div>

                <div id="reach-brand-safety" class="settings-block">
                    <ReachBrandSafety :creative-type="mainSettingsData.creative_type" :placements="metaPlacements"
                        :initial-reach="reachData" :blocklists="blocklists" @update="onReachUpdate"
                        ref="reachBrandSafety" />
                </div>

                <div id="audience-geography" class="settings-block">
                    <AudienceGeography :campaign="adGroup" :initial-data="audienceData" :audiences="audiences"
                        :geographies="geographies" @update="onAudienceUpdate" />
                </div>

                <div id="device-targeting" class="settings-block">
                    <DeviceTargeting :campaign="adGroup" :device-types="deviceTypes"
                        :operating-systems="operatingSystems" @update="onDeviceUpdate" />
                </div>

                <div id="commissions" class="settings-block" v-if="isAltel">
                    <Commissions @update="onCommissionsUpdate" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { parseApiError } from '../../../utils/apiError';
import MainSettings from './Steps/MainSettings.vue';
import BudgetSchedule from './Steps/BudgetSchedule.vue';
import ReachBrandSafety from './Steps/ReachBrandSafety.vue';
import AudienceGeography from './Steps/AudienceGeography.vue';
import DeviceTargeting from './Steps/DeviceTargeting.vue';
import Commissions from './Steps/Commissions.vue';

export default {
    name: "AdGroupCreate",
    components: {
        MainSettings,
        BudgetSchedule,
        ReachBrandSafety,
        AudienceGeography,
        DeviceTargeting,
        Commissions,
    },
    props: {
        mode: { type: String, default: '' },
        adGroup: { type: Object, default: () => ({}) },
        campaign: { type: Object, default: () => ({}) },
        prices: { type: Array, default: () => [] },
        audiences: { type: Array, default: () => [] },
        metaPlacements: { type: Array, default: () => [] },
        geographies: { type: Array, default: () => [] },
        environments: { type: Array, default: () => [] },
        deviceTypes: { type: Array, default: () => [] },
        operatingSystems: { type: Array, default: () => [] },
        blocklists: { type: Array, default: () => [] },
        currencies: { type: Array, default: () => [] },
        user: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            currentStep: 'main-settings',
            fullscreenLoading: false,
            isSticky: false,
            submitting: false,
            // aggregated data from child blocks
            mainSettingsData: {},
            budgetScheduleData: {
                currency: 'KZT',
                paymentValue: 10.00,
                totalBudget: 0.00,
                schedule: [],
                lifeTimeDistribution: 'evenly'
            },
            reachData: {
                blocklists: [],
                whitelists: [],
                frequency: null,
                period_type: 'day',
                period_value: 1,
                frequency_is_active: false,
                viewability: false,
                viewability_percentage: 0,
                meta_placements: []
            },
            audienceData: {
                geography: [],
                audiences: []
            },
            deviceData: {
                viewability: false,
                viewability_percentage: 0,
                devices: [],
                operatingSystemIds: []
            },
            commissionsData: {
                commissions: []
            },
            isInitialized: false,
        };
    },
    computed: {
        isEditMode() {
            return this.mode === 'edit';
        },
        adGroupId() {
            return this.adGroup && this.adGroup.id ? Number(this.adGroup.id) : null;
        },
        isAltel() {
            return this.user && (this.user.role_id === 2 || this.user.role === 'altel');
        },
        isTestCampaign() {
            return !!(this.campaign && (this.campaign.is_test ?? this.campaign.isTest));
        },
        // whether form is valid for creation
        canCreate() {
            // main settings
            const ms = this.mainSettingsData || {};
            const nameOk = ms.name && String(ms.name).trim().length > 0;
            const creativeOk = !!ms.creative_type;
            const envOk = !!ms.environment;
            const formatOk = !!ms.ad_format;
            const kpiOk = !!ms.kpi;
            // price value present (safety)
            const priceOk = ms.price_value !== null && ms.price_value !== undefined;

            // dates
            const startOk = !!ms.start_date;
            const endOk = !!ms.end_date;
            let datesOk = false;
            if (startOk && endOk) {
                try {
                    const s = new Date(ms.start_date);
                    const e = new Date(ms.end_date);
                    datesOk = !isNaN(s.valueOf()) && !isNaN(e.valueOf()) && e >= s;
                } catch (e) {
                    datesOk = false;
                }
            }

            // schedule: at least one period with a valid budget (0 allowed for test campaigns)
            const sched = (this.budgetScheduleData && Array.isArray(this.budgetScheduleData.schedule)) ? this.budgetScheduleData.schedule : [];
            const hasPeriod = sched.length > 0;
            const periodBudgetOk = hasPeriod && sched.every(p => {
                if (!p || p.budget === undefined || p.budget === null || p.budget === '') return false;
                const budget = Number(p.budget);
                return !isNaN(budget) && budget >= 0;
            }) && (this.isTestCampaign || sched.some(p => Number(p.budget) > 0));

            // geography: at least one selected
            const geo = this.audienceData && Array.isArray(this.audienceData.geography) ? this.audienceData.geography : [];
            const geoOk = geo.length > 0;

            return nameOk && creativeOk && envOk && formatOk && kpiOk && priceOk && datesOk && hasPeriod && periodBudgetOk && geoOk;
        },
        steps() {
            const s = [
                { id: 'main-settings', title: this.$t('messages.main_settings'), icon: '/images/settings.svg' },
                { id: 'budget-schedule', title: this.$t('messages.budget_and_schedule') || 'Бюджет и расписание', icon: '/images/calendar-coin.svg' },
                { id: 'reach-brand-safety', title: this.$t('messages.reach_brand_safety_adgroup') || 'Настройки охвата и бренд-безопасности', icon: '/images/globe-lock.svg' },
                { id: 'audience-geography', title: this.$t('messages.audience_geography') || 'Аудитория и география', icon: '/images/users.svg' },
                { id: 'device-targeting', title: this.$t('messages.device_targeting_title') || 'Видимость и таргетинг по устройствам', icon: '/images/eye.svg' }
            ];
            if (this.isAltel) {
                s.push({ id: 'commissions', title: this.$t('messages.commissions'), icon: '/images/percent.svg' });
            }
            return s;
        }
    },
    mounted() {
        this.setupScrollListener();
        if (this.isEditMode && this.adGroup) {
            this.populateFromAdGroup(this.adGroup);
            this.$nextTick(() => { this.isInitialized = true; });
            return;
        }
        // not edit mode -> if initial audienceIds provided, populate audienceData
        if ((!this.isEditMode) && Array.isArray(this.audienceIds) && this.audienceIds.length) {
            this.audienceData = { geography: [], audience_ids: this.audienceIds };
        }
        // ready
        this.isInitialized = true;
    },
    watch: {
        adGroup(val) {
            if (this.isEditMode && val) {
                this.populateFromAdGroup(val);
                this.$nextTick(() => { this.isInitialized = true; });
            }
        }
    },
    beforeUnmount() {
        this.removeScrollListener();
    },
    methods: {
        populateFromAdGroup(ag) {
            if (!ag) return;
            // Main settings mapping
            this.mainSettingsData = {
                campaign_id: ag.campaign_id ?? null,
                creative_type: ag.creative_type ?? '',
                environment: ag.environment ?? '',
                ad_format: ag.ad_format ?? '',
                kpi: ag.kpi ?? '',
                price_id: ag.price_id ?? null,
                price_value: ag.price_value ?? null,
                name: ag.name ?? '',
                status: ag.status ?? 'active',
                total_budget: ag.total_budget ?? null,
                planned_volume: ag.planned_volume != null ? ag.planned_volume : null,
                start_date: ag.start_date ? ag.start_date.split('T')[0] : null,
                end_date: ag.end_date ? ag.end_date.split('T')[0] : null
            };

            // Budget / schedule
            this.budgetScheduleData = {
                currency: ag.currency ?? 'KZT',
                paymentValue: ag.payment_value ?? 10.00,
                totalBudget: ag.total_budget != null ? ag.total_budget : 0,
                schedule: (ag.schedules || []).map(s => ({
                    startDate: s.start_date ? s.start_date.split('T')[0] : null,
                    startTime: s.start_time || '00:00',
                    endDate: s.end_date ? s.end_date.split('T')[0] : null,
                    endTime: s.end_time || '23:59',
                    budget: s.budget != null ? parseFloat(s.budget) : 0,
                    planned_volume: s.planned_volume != null ? parseInt(s.planned_volume, 10) : null
                })),
                lifeTimeDistribution: ag.lifetime_distribution ?? 'evenly'
            };

            // Normalize helpers
            const normalizeIds = (arr) => {
                if (!Array.isArray(arr)) return [];
                if (arr.length === 0) return [];
                if (typeof arr[0] === 'object' && arr[0] !== null && 'id' in arr[0]) {
                    return arr.map(x => Number(x.id)).filter(Boolean);
                }
                return arr.map(x => Number(x)).filter(Boolean);
            };

            const blocklistItems = Array.isArray(ag.blocklists) ? ag.blocklists : [];
            const blackFromPivot = [];
            const whiteFromPivot = [];
            blocklistItems.forEach((item) => {
                const id = typeof item === 'object' ? Number(item.id) : Number(item);
                if (!id) return;
                const type = item?.pivot?.type ?? item?.type ?? 'black';
                if (type === 'white') {
                    whiteFromPivot.push(id);
                } else {
                    blackFromPivot.push(id);
                }
            });

            const whiteIds = normalizeIds(ag.whitelists);
            const blackIds = whiteIds.length > 0 ? normalizeIds(ag.blocklists) : (blackFromPivot.length ? blackFromPivot : normalizeIds(ag.blocklists));

            // Reach / brand safety - normalize fields expected by frontend children
            this.reachData = {
                blocklists: blackIds,
                whitelists: whiteIds.length > 0 ? whiteIds : whiteFromPivot,
                frequency: ag.frequency ?? null,
                period_type: ag.period_type ?? 'day',
                period_value: ag.period_value ?? 1,
                frequency_is_active: !!ag.frequency_is_active,
                viewability: ag.viewability ?? false,
                viewability_percentage: ag.viewability_percentage ?? 0,
                meta_placements: Array.isArray(ag.meta_placements) ? normalizeIds(ag.meta_placements) : (Array.isArray(ag.audience_ids) ? normalizeIds(ag.audience_ids) : [])
            };

            // Audience & geography
            let geographyIds = [];
            if (Array.isArray(ag.geographies)) {
                if (ag.geographies.length && typeof ag.geographies[0] === 'object') {
                    geographyIds = ag.geographies.map(g => Number(g.id)).filter(Boolean);
                } else {
                    geographyIds = ag.geographies.map(g => Number(g)).filter(Boolean);
                }
            }
            let audienceIds = [];
            if (Array.isArray(ag.audiences) && ag.audiences.length) {
                audienceIds = ag.audiences.map(a => (a && a.id) ? Number(a.id) : Number(a)).filter(Boolean);
            } else if (Array.isArray(ag.audience_ids) && ag.audience_ids.length) {
                audienceIds = ag.audience_ids.map(Number).filter(Boolean);
            }
            this.audienceData = {
                geography: geographyIds,
                audiences: Array.from(new Set(audienceIds))
            };

            // Device targeting
            this.deviceData = {
                viewability: ag.viewability ?? false,
                viewability_percentage: ag.viewability_percentage ?? 0,
                devices: ag.device_types ? ag.device_types.map(d => Number(d.id)) : (this.deviceTypes || []).map(d => Number(d.id)),
                operatingSystemIds: ag.operating_systems ? ag.operating_systems.map(o => Number(o.id)) : []
            };

            // Commissions
            this.commissionsData = ag.commissions ? { commissions: ag.commissions } : {};
        },
        goToStep(stepId) {
            this.currentStep = stepId;
            const element = document.getElementById(stepId);
            if (element) {
                const headerHeight = 70;
                const offsetTop = element.offsetTop - headerHeight - 20;
                window.scrollTo({ top: offsetTop, behavior: 'smooth' });
            }
        },
        setupScrollListener() {
            this.scrollHandler = this.handleScroll.bind(this);
            window.addEventListener('scroll', this.scrollHandler);
        },
        removeScrollListener() {
            if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
        },
        handleScroll() {
            const scrollPosition = window.scrollY;
            const windowHeight = window.innerHeight;
            const headerHeight = 70;
            this.isSticky = scrollPosition > headerHeight;
            const sections = this.steps.map(step => ({ id: step.id, element: document.getElementById(step.id) }));
            let newStep = 'main-settings';
            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section.element) {
                    const rect = section.element.getBoundingClientRect();
                    const elementTop = rect.top + scrollPosition;
                    if (scrollPosition >= elementTop - windowHeight / 2) {
                        newStep = section.id;
                        break;
                    }
                }
            }
            if (newStep !== this.currentStep) this.currentStep = newStep;
        },
        async saveAdGroup() {
            // show fullscreen loader
            this.fullscreenLoading = true;
            this.submitting = true;
            try {
                // basic validation before sending
                if (!this.isEditMode && !this.canCreate) {
                    this.$notify({
                        title: this.$t('messages.validation_error_title') || 'Validation',
                        message: this.$t('messages.error_fill_required_fields') || 'Please fill required fields before creating.',
                        type: 'error'
                    });
                    return;
                }
                const raw = {
                    ...(this.mainSettingsData || {}),
                    schedule: (this.budgetScheduleData && this.budgetScheduleData.schedule) ? this.budgetScheduleData.schedule : [],
                    ...(this.reachData || {}),
                    audience: this.audienceData || {},
                    device: this.deviceData || {},
                    commissions: this.commissionsData || {}
                };

                // normalize keys expected by backend
                const payload = { ...raw };
                // geographies
                if (raw.audience && Array.isArray(raw.audience.geography)) {
                    payload.geographies = raw.audience.geography;
                }
                // audiences: expect flat array provided by AudienceGeography ({ audiences: [...] })
                if (raw.audience && Array.isArray(raw.audience.audiences)) {
                    payload.audiences = raw.audience.audiences;
                } else {
                    payload.audiences = [];
                }
                // device types
                if (raw.device && Array.isArray(raw.device.devices)) {
                    payload.device_types = raw.device.devices;
                }
                // operating systems (from DeviceTargeting.operatingSystemIds)
                if (raw.device && Array.isArray(raw.device.operatingSystemIds)) {
                    payload.operating_systems = raw.device.operatingSystemIds;
                }
                // viewability may come from DeviceTargeting (nested under device) — lift to top-level
                if (raw.device && (raw.device.viewability !== undefined || raw.device.viewability_percentage !== undefined)) {
                    payload.viewability = !!raw.device.viewability;
                    payload.viewability_percentage = Number(raw.device.viewability_percentage || 0);
                }
                // environments: backend expects array
                if (payload.environment && !Array.isArray(payload.environments)) {
                    payload.environments = [payload.environment];
                }
                // blocklists / whitelists from reach data
                if (raw.blocklists) payload.blocklists = raw.blocklists;
                if (raw.whitelists) payload.whitelists = raw.whitelists;
                // meta placements are sent separately (do not merge into audiences)
                if (raw.meta_placements && Array.isArray(raw.meta_placements)) {
                    payload.meta_placements = raw.meta_placements.map(Number).filter(Boolean);
                }

                // remove nested client-only structures to avoid confusion
                delete payload.audience;
                delete payload.device;
                delete payload.commissions;

                const token = document.querySelector('meta[name=\"csrf-token\"]').getAttribute('content');
                if (this.isEditMode) {
                    if (!this.adGroupId) {
                        throw new Error('No ad group id for update');
                    }
                    await axios.put(`/ad-groups/${this.adGroupId}`, payload, { headers: { 'X-CSRF-TOKEN': token } });
                } else {
                    const campaignId = payload.campaign_id || (this.mainSettingsData && this.mainSettingsData.campaign_id);
                    if (!campaignId) {
                        throw new Error('No campaign selected for ad group');
                    }
                    await axios.post(`/campaigns/${campaignId}/ad-groups/create`, payload, { headers: { 'X-CSRF-TOKEN': token } });
                }
                this.$notify({
                    title: this.$t('messages.completed_successfully'),
                    message: this.isEditMode
                        ? this.$t('messages.ad_group_updated_successfully')
                        : this.$t('messages.ad_group_created_successfully'),
                    type: 'success'
                });
                // redirect back to campaigns overview
                window.location.href = '/campaigns';
            } catch (err) {
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: parseApiError(err, this.$t('messages.error') || 'Error'),
                    type: 'error',
                });
            } finally {
                this.submitting = false;
                this.fullscreenLoading = false;
            }
        },
        onCreated(payload) {
            this.$emit('created', payload);
        },
        onMainUpdate(data) {
            this.mainSettingsData = data || {};
        },
        onBudgetUpdate(data) {
            this.budgetScheduleData = data || {};
        },
        onReachUpdate(data) {
            this.reachData = data || {};
        },
        onAudienceUpdate(data) {
            this.audienceData = data || {};
        },
        onDeviceUpdate(data) {
            this.deviceData = data || {};
        },
        onCommissionsUpdate(data) {
            this.commissionsData = data || {};
        }
    }
};
</script>